import React, { FC, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Box, IconButton, InputAdornment, Paper, Typography, alpha, useTheme } from '@mui/material';
import { MdOutlinePrecisionManufacturing, MdOutlineStorefront, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { Input } from '../../Components/Form/Input/input.component';
import { Button } from '../../Components/Form/Button/button.scomponent';
import Brand from '../../Assets/brand.webp';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../Hooks/Auth';
import { ForgotPasswordModal } from '../../Components/ForgotPassword/forgot-password-modal.component';
import { LoginUseCase } from '../../Usecase/Auth/login-usecase';
import { RequestPasswordResetUseCase } from '../../Usecase/Auth/request-password-reset-usecase';
import { useToast } from '../../Hooks/ToasContext';
import { adminVersion } from '../../Services/api';

interface SignInProps {
  login: string;
  password: string;
}

const SignInSchemaValidation = Yup.object().shape({
  login: Yup.string().required('Login obrigatório'),
  password: Yup.string().required('Senha obrigatória'),
});

interface Props {
  loginUseCase: LoginUseCase;
  requestPasswordResetUseCase: RequestPasswordResetUseCase;
}

const inputAutofillSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#fff',
    borderRadius: 2,
  },
  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
    WebkitBoxShadow: '0 0 0 100px #ffffff inset',
    WebkitTextFillColor: '#1a1a1a',
    caretColor: '#1a1a1a',
    transition: 'background-color 9999s ease-out 0s',
  },
};

const platformModules = [
  {
    icon: MdOutlineStorefront,
    code: 'HUB',
    label: 'Gestão comercial',
    items: ['Pedidos', 'Clientes', 'Financeiro'],
  },
  {
    icon: MdOutlinePrecisionManufacturing,
    code: 'HCP',
    label: 'Gestão de produção',
    items: ['Arte', 'Produção', 'Logística'],
  },
];

const SignIn: FC<Props> = ({ loginUseCase, requestPasswordResetUseCase }) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();
  const { showToast } = useToast();
  const { signIn } = useAuth();

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<SignInProps>({
    resolver: yupResolver(SignInSchemaValidation),
  });

  const handleLogin: SubmitHandler<SignInProps> = async (values) => {
    try {
      setLoading(true);
      const loginResult = await loginUseCase.handle({ username: values.login, password: values.password });

      if (loginResult.isFailure) {
        showToast(String(loginResult?.error?.error), 'error');
        return;
      }

      const result = loginResult.getValue();
      if (!result) {
        showToast('Erro ao realizar login', 'error');
        return;
      }

      signIn(result.sessionToken, {
        username: result.username,
        permissions: result.permissions || [],
        permissionsScreens: result.permissionsScreens || [],
        resetPassword: result.resetPassword || false,
      });

      navigate('/dashboard');
    } catch (error) {
      showToast('Erro ao fazer login. Por favor, tente novamente.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setForgotPasswordOpen(true);
  };

  const handleRequestPasswordReset = async (username: string) => {
    setForgotPasswordLoading(true);

    try {
      const result = await requestPasswordResetUseCase.handle({ username });

      if (result.isFailure) {
        const err = result.error as { error?: string; message?: string; statusCode?: number };
        showToast(String(err?.error || err?.message || 'Não foi possível solicitar a recuperação.'), 'error');
        return;
      }

      showToast(
        result.getValue()?.message || 'Se existir uma conta com esse e-mail, enviaremos instruções para redefinir a senha.',
        'success'
      );
      setForgotPasswordOpen(false);
    } catch {
      showToast('Não foi possível solicitar a recuperação. Tente novamente.', 'error');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <>
      <ForgotPasswordModal
        open={forgotPasswordOpen}
        loading={forgotPasswordLoading}
        onClose={() => setForgotPasswordOpen(false)}
        onSubmit={handleRequestPasswordReset}
      />
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, sm: 3 },
          py: { xs: 3, sm: 4 },
          backgroundColor: '#eef2e6',
          backgroundImage: `
            radial-gradient(circle at 15% 20%, ${alpha(theme.palette.primary.main, 0.18)} 0%, transparent 42%),
            radial-gradient(circle at 85% 80%, ${alpha(theme.palette.primary.dark, 0.12)} 0%, transparent 38%)
          `,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 1000,
            borderRadius: { xs: 3, sm: 4 },
            overflow: 'hidden',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            minHeight: { md: 620 },
            border: '1px solid',
            borderColor: alpha('#000', 0.06),
            boxShadow: '0 24px 80px rgba(0, 0, 0, 0.10)',
          }}
        >
          {/* Painel esquerdo */}
          <Box
            sx={{
              flex: { md: '1 1 50%' },
              p: { xs: 3, sm: 4, md: 5 },
              color: '#fff',
              display: 'flex',
              flexDirection: 'column',
              background: `linear-gradient(155deg, ${theme.palette.primary.dark} 0%, #3f5606 55%, #2d3f04 100%)`,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Elementos decorativos de fundo */}
            <Box sx={{ position: 'absolute', width: 340, height: 340, borderRadius: '50%', bgcolor: alpha('#fff', 0.04), top: -110, right: -110, pointerEvents: 'none' }} />
            <Box sx={{ position: 'absolute', width: 220, height: 220, borderRadius: '50%', bgcolor: alpha('#fff', 0.03), bottom: -70, left: -70, pointerEvents: 'none' }} />

            {/* Conteúdo superior: logo + título + subtítulo */}
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box
                component="img"
                src={Brand}
                alt="Papello"
                sx={{ width: { xs: 130, sm: 148 }, filter: 'brightness(0) invert(1)', mb: 4, display: 'block' }}
              />
              <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: -0.5, lineHeight: 1.15, mb: 1.5, maxWidth: 340 }}>
                Papello Embalagens
              </Typography>
              <Typography sx={{ opacity: 0.78, lineHeight: 1.75, maxWidth: 360, fontSize: '0.9rem' }}>
                Sistema de gestão integrado para toda a equipe.
              </Typography>
            </Box>

            {/* Conteúdo inferior: cards + versão */}
            <Box sx={{ position: 'relative', zIndex: 1, mt: 'auto', pt: 5 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 3 }}>
                {platformModules.map((module) => {
                  const Icon = module.icon;
                  return (
                    <Box
                      key={module.code}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha('#fff', 0.07),
                        border: `1px solid ${alpha('#fff', 0.12)}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 1.5,
                            bgcolor: alpha('#fff', 0.13),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Icon size={17} />
                        </Box>
                        <Box>
                          <Typography fontWeight={800} fontSize={13} lineHeight={1.2} letterSpacing={0.8}>
                            {module.code}
                          </Typography>
                          <Typography fontSize={11} sx={{ opacity: 0.6 }}>
                            {module.label}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography fontSize={11.5} sx={{ opacity: 0.72, lineHeight: 1.6 }}>
                        {module.items.join(' · ')}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              <Box sx={{ pt: 2, borderTop: `1px solid ${alpha('#fff', 0.1)}` }}>
                <Typography variant="caption" sx={{ opacity: 0.42, letterSpacing: 0.3 }}>
                  Versão {adminVersion} · Papello Embalagens
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Painel direito — formulário */}
          <Box
            component="form"
            onSubmit={handleSubmit(handleLogin)}
            sx={{
              flex: { md: '1 1 50%' },
              p: { xs: 3, sm: 4, md: 6 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              bgcolor: '#fff',
            }}
          >
            <Box sx={{ width: '100%', maxWidth: 360, mx: 'auto' }}>
              <Typography variant="h5" fontWeight={800} mb={0.75} sx={{ letterSpacing: -0.3 }}>
                Acessar
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={4} lineHeight={1.6}>
                Insira suas credenciais para continuar.
              </Typography>

              <Input
                label="E-mail"
                placeholder="seu.email@papello.com.br"
                autoComplete="email"
                sx={{ ...inputAutofillSx, mb: 2 }}
                error={!!errors.login}
                helperText={errors.login?.message}
                {...register('login')}
              />
              <Input
                label="Senha"
                placeholder="Digite sua senha"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                sx={{ ...inputAutofillSx, mb: 3 }}
                error={!!errors.password}
                helperText={errors.password?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <MdVisibilityOff size={20} /> : <MdVisibility size={20} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                {...register('password')}
              />

              <Button type="submit" sx={{ width: '100%' }} isLoading={loading}>
                Acessar plataforma
              </Button>

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography
                  component="span"
                  variant="body2"
                  onClick={handleForgotPassword}
                  sx={{
                    color: 'text.secondary',
                    cursor: 'pointer',
                    transition: 'color 0.2s',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  Esqueci minha senha
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export { SignIn };
